// Host payment details (location_payment): at host-run locations participants
// pay the host directly instead of our Stripe checkout. One decision for the
// registration email, the reminder emails and the site. Pure, so the browser
// imports it via `@shared/hostPayment`.
import { escapeHtml } from "./email.ts";

/** A location_payment row (or get_event_payment_info result). */
export interface HostPaymentRow {
  pay_url: string | null;
  pay_note: string | null;
}

export interface HostPayment {
  url: string | null;
  note: string | null;
}

/** Same rule as the DB CHECK on location_payment.pay_url. */
export function isHttpUrl(url: string): boolean {
  return /^https?:\/\//.test(url);
}

/**
 * Trimmed host details, or null when there are none (pay us via Stripe as
 * before). A URL that isn't http(s) is dropped.
 */
export function hostPayment(row: HostPaymentRow | null | undefined): HostPayment | null {
  const rawUrl = row?.pay_url?.trim() || null;
  const url = rawUrl && isHttpUrl(rawUrl) ? rawUrl : null;
  const note = row?.pay_note?.trim() || null;
  return url || note ? { url, note } : null;
}

/** "Pay host (£10.00)", or "Pay host" without a price. */
export function hostPayLabel(priceLabel: string | null | undefined): string {
  return priceLabel ? `Pay host (${priceLabel})` : "Pay host";
}

/**
 * Email HTML for paying the host: the note (escaped, newlines kept) above a
 * button to the host's link. Without a link the price goes in its own line,
 * since there's no button to carry it.
 */
export function hostPaymentHtml(
  payment: HostPayment,
  priceLabel: string | null | undefined,
  buttonStyle: string,
): string {
  const amount = !payment.url && priceLabel
    ? `<p style="margin: 0 0 8px;"><b>Amount: ${escapeHtml(priceLabel)}</b></p>`
    : "";
  const note = payment.note
    ? `<p style="margin: 0 0 12px;">${escapeHtml(payment.note).replace(/\r?\n/g, "<br>")}</p>`
    : "";
  const button = payment.url
    ? `<a href="${escapeHtml(payment.url)}" style="${buttonStyle}">${escapeHtml(hostPayLabel(priceLabel))}</a>`
    : "";
  return `${amount}${note}${button}`;
}
